// Player settings (spec 2026-10-27 §3): the grey gear under PEACEFUL on every
// Hub chapter opens a panel of toggles. They are a DEVICE preference — kept in
// `hambi.settings`, never in SYNCED_KEYS, so logging out does not wipe them
// and they never reach game_saves.
//
// Disable Animation skips the TOWER cutscenes (King Hambi's K–O, Ultra Hammy's
// END THEM, the Phantom's red-bullet cinematic, the Golden Shroom's Shroom
// Frenzy) in every level. The attack itself is unchanged: its damage is
// timed in SIM seconds after the cast, and the cutscenes only ever paused the
// sim, so skipping one moves no hit by a single tick. Level cutscenes (Prince
// Nacho, the rocket, King Shroometron's fall) still play.

export const SETTINGS_KEY = 'hambi.settings';

export interface GameSettings {
  disableAnimations: boolean;
}

export const DEFAULT_SETTINGS: GameSettings = { disableAnimations: false };

/** The toggles the Settings panel draws, in order. */
export const SETTING_TOGGLES: readonly {
  key: keyof GameSettings;
  label: string;
  hint: string;
}[] = [
  {
    key: 'disableAnimations',
    label: 'Disable Animation',
    hint: 'Skips tower cutscenes. The attacks still happen the same way.',
  },
];

type Store = Pick<Storage, 'getItem' | 'setItem'>;

function store(): Store | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readSettings(s: Store | null = store()): GameSettings {
  try {
    const raw = s?.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const v = JSON.parse(raw) as Partial<GameSettings>;
    return { disableAnimations: v.disableAnimations === true };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function writeSetting<K extends keyof GameSettings>(
  key: K,
  value: GameSettings[K],
  s: Store | null = store(),
): GameSettings {
  const next = { ...readSettings(s), [key]: value };
  try {
    s?.setItem(SETTINGS_KEY, JSON.stringify(next));
  } catch {
    // Private windows can refuse storage; the toggle just lasts this session.
  }
  return next;
}

/** True when tower cutscenes should be skipped. */
export function animationsDisabled(s: Store | null = store()): boolean {
  return readSettings(s).disableAnimations;
}
